/**
 * Collectr portfolio CSV importer.
 *
 * Parses a Collectr export and converts each row into the shape used by the
 * Purchase form's Items[] or the Trade form's Incoming[] - reviewed in the
 * form before save, never written to inventory directly. Always creates new
 * lots (no merge-with-existing).
 *
 * Collectr export columns (as of 2026):
 *   Portfolio Name, Category, Set, Product Name, Card Number, Rarity,
 *   Variance, Grade, Card Condition, Average Cost Paid, Quantity,
 *   Market Price (As of YYYY-MM-DD), Price Override, Watchlist, Date Added, Notes
 *
 * Field mapping notes:
 *   - Game            = Category (passed through; we accept Pokemon and others)
 *   - Item / Name     = Product Name
 *   - Set             = Set
 *   - Number          = Card Number (the slash suffix like "245/217" is kept verbatim)
 *   - Condition       = if Grade is "Ungraded" -> Card Condition (Near Mint, etc.)
 *                       otherwise -> Grade (PSA 10, BGS 9.5, etc.)
 *   - Qty             = Quantity
 *   - UnitCost / FMV  = Average Cost Paid (basis) / Market Price (FMV)
 *   - Notes carry the Rarity + Variance so users don't lose that info; the
 *     original Collectr Notes column (free text) is appended too.
 *
 * The parser is intentionally tolerant: header names are matched
 * case-insensitively with whitespace and the "(As of YYYY-MM-DD)" suffix
 * stripped, so Collectr can rename the snapshot date and we still find the
 * Market Price column.
 */
import { toast } from "./dom";
import { todayISO } from "../utils/format";

/** Raw row pulled out of the Collectr CSV. Everything is a string until
 *  mapping converts numbers + dates. */
export interface CollectrRow {
  portfolio:   string;
  category:    string;   // Pokemon, Magic, etc.
  set:         string;
  productName: string;
  cardNumber:  string;
  rarity:      string;
  variance:    string;   // Normal, Holofoil, Reverse Holofoil
  grade:       string;   // Ungraded, PSA 10, etc.
  condition:   string;   // Near Mint, Lightly Played, etc.
  avgCostPaid: number;   // unit basis
  quantity:    number;
  marketPrice: number;   // unit FMV
  dateAdded:   string;   // YYYY-MM-DD (or empty)
  notes:       string;
}

/** Item shape expected by the Purchase form's Items[] array. Mirrors
 *  forms/purchase.ts's PurchaseItem interface (kept loose to avoid import
 *  cycles - the form merges this with its own defaults). */
export interface ImportedPurchaseItem {
  Type: "Raw" | "Graded";
  Game: string;
  Item: string;
  Set: string;
  Number: string;
  Condition: string;
  Grade: string;
  ProductType: string;
  Qty: number;
  UnitCost: number;
}

/** Item shape expected by the Trade form's Incoming[] array. Mirrors
 *  forms/trade.ts's IncomingItem interface for the Card / Graded paths. */
export interface ImportedTradeIncomingItem {
  AsType: "Card";
  Game: string;
  Item: string;
  Set: string;
  Number: string;
  Condition: string;
  ProductType: string;
  Image: string;
  Qty: number;
  FMV: number;
  CardKind: "Raw" | "Graded";
}

/** Item shape expected by the Adjustment form's Items[] array when in
 *  Owner Contribution "in" mode. Mirrors views/transactions.ts's TxnItem
 *  interface. ContribFMV is the fair market value at contribution time -
 *  that's what gets credited to "what the owner is owed." UnitBasis is the
 *  original cost (used for COGS when the lot is later sold); the form
 *  defaults UnitBasis to ContribFMV at save if left at 0. */
export interface ImportedAdjustmentItem {
  AsType: "Card";
  Game: string;
  Item: string;
  Set: string;
  Number: string;
  Condition: string;
  ProductType: string;
  Qty: number;
  UnitBasis: number;
  ContribFMV: number;
}

/* ============================================================
   CSV parser (tiny, RFC-4180-ish; handles quoted fields + commas
   inside quotes + escaped double-quotes). Returns an array of
   rows where each row is an array of cell strings.
   ============================================================ */
function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  const len = text.length;
  let i = 0;
  // Strip a UTF-8 BOM if present so it doesn't end up in the first column.
  if (text.charCodeAt(0) === 0xFEFF) i = 1;
  while (i < len) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',')  { row.push(field); field = ""; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
    field += c; i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

/** Normalize a header name to a stable lookup key: lowercased, whitespace
 *  collapsed, and the "(As of YYYY-MM-DD)" suffix removed so the Market Price
 *  column keeps matching even when Collectr updates the snapshot date. */
function normalizeHeader(h: string): string {
  return String(h || "")
    .replace(/\(as of [\d-]+\)/i, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Map normalized header keys to CollectrRow field names. */
const HEADER_TO_FIELD: Record<string, keyof CollectrRow> = {
  "portfolio name":    "portfolio",
  "category":          "category",
  "set":               "set",
  "product name":      "productName",
  "card number":       "cardNumber",
  "rarity":            "rarity",
  "variance":          "variance",
  "grade":             "grade",
  "card condition":    "condition",
  "average cost paid": "avgCostPaid",
  "quantity":          "quantity",
  "market price":      "marketPrice",
  "date added":        "dateAdded",
  "notes":             "notes",
};

const NUMERIC_FIELDS: Array<keyof CollectrRow> = ["avgCostPaid", "quantity", "marketPrice"];

/** Parse a Collectr CSV string into typed rows. Throws if the header row
 *  doesn't include at least Product Name, Set, and Quantity (the minimum
 *  needed to identify the file as a Collectr-shaped export). */
export function parseCollectrCsv(text: string): CollectrRow[] {
  const raw = parseCsvText(text).filter(r => r.some(c => c && c.trim() !== ""));
  if (raw.length < 2) throw new Error("CSV looks empty (no data rows).");
  const headers = raw[0].map(normalizeHeader);
  // Sanity check the shape.
  const must = ["product name", "set", "quantity"];
  for (const m of must) {
    if (!headers.includes(m)) {
      throw new Error(`Not a Collectr export - missing "${m}" column.`);
    }
  }
  const out: CollectrRow[] = [];
  for (let r = 1; r < raw.length; r++) {
    const cells = raw[r];
    const row: CollectrRow = {
      portfolio: "", category: "", set: "", productName: "", cardNumber: "",
      rarity: "", variance: "", grade: "", condition: "",
      avgCostPaid: 0, quantity: 0, marketPrice: 0,
      dateAdded: "", notes: "",
    };
    for (let c = 0; c < headers.length; c++) {
      const field = HEADER_TO_FIELD[headers[c]];
      if (!field) continue;
      const raw = (cells[c] || "").trim();
      if (NUMERIC_FIELDS.includes(field)) {
        (row as any)[field] = Number(raw) || 0;
      } else {
        (row as any)[field] = raw;
      }
    }
    // Drop blank rows (no name + no qty).
    if (!row.productName && !row.quantity) continue;
    out.push(row);
  }
  return out;
}

/** Helper: decide condition vs grade based on the Grade column. Collectr uses
 *  "Ungraded" for raw cards and otherwise a slab string like "PSA 10.0 GEM - MT". */
function isGraded(row: CollectrRow): boolean {
  const g = (row.grade || "").trim().toLowerCase();
  return !!g && g !== "ungraded";
}

/** Normalize a Collectr grade string to one of the app's grade-select options.
 *  Collectr uses richer labels like "PSA 10.0 GEM - MT" / "CGC 10.0 Pristine" /
 *  "BGS 9.5 Gem Mint" - extract the grader + numeric and rebuild as "PSA 10".
 *  Returns the raw value unchanged if it doesn't match the known graders so
 *  exotic grades fall through to the "Other" option. */
function normalizeGrade(raw: string): string {
  const g = String(raw || "").trim();
  const m = g.match(/^(PSA|BGS|CGC|SGC)\s+(\d+(?:\.\d+)?)/i);
  if (!m) return g;
  const grader = m[1].toUpperCase();
  // "10.0" -> "10"; "9.5" stays "9.5"; "9.0" -> "9"
  const num = m[2].replace(/\.0$/, "");
  return `${grader} ${num}`;
}

/** Normalize a Collectr Card Condition string to one of the app's Condition
 *  options ["Near Mint","Mint","Lightly Played","Moderately Played","Heavily Played","Damaged","Other"].
 *  Collectr uses "Moderately Played" / "Heavily Played" which we collapse to
 *  the closest available bucket so the select shows the imported value. */
function normalizeCondition(raw: string): string {
  const c = String(raw || "").trim();
  const lc = c.toLowerCase();
  if (!c) return "Near Mint";
  // Direct matches first
  if (lc === "near mint" || lc === "nm")             return "Near Mint";
  if (lc === "mint" || lc === "m")                   return "Mint";
  if (lc === "lightly played" || lc === "lp")        return "Lightly Played";
  if (lc === "moderately played" || lc === "mp")     return "Moderately Played";
  if (lc === "heavily played"    || lc === "hp")     return "Heavily Played";
  if (lc === "damaged" || lc === "dmg")              return "Damaged";
  // Legacy fallback: a bare "Played" (older condition naming) becomes MP -
  // it's the closest bucket in the new three-tier scheme.
  if (lc === "played" || lc === "pl")                return "Moderately Played";
  if (lc === "poor")                                  return "Damaged";
  // Unknown - fall through to "Other" (recognized by the select)
  return "Other";
}

/** Build the Condition/Grade string for a Collectr row. For graded slabs this
 *  is the normalized Grade ("PSA 10.0 GEM - MT" -> "PSA 10"); for raw it's the
 *  normalized Card Condition. */
function conditionFor(row: CollectrRow): string {
  return isGraded(row) ? normalizeGrade(row.grade) : normalizeCondition(row.condition);
}

/** Build a Notes string that preserves the bits we can't fit elsewhere -
 *  Rarity, Variance (if non-Normal), and Collectr's own Notes. */
function notesFor(row: CollectrRow): string {
  const parts: string[] = [];
  if (row.rarity)  parts.push(row.rarity);
  const v = (row.variance || "").trim();
  if (v && v.toLowerCase() !== "normal") parts.push(v);
  if (row.notes)   parts.push(row.notes);
  return parts.join(" · ");
}

/** Map a parsed Collectr row to a PurchaseItem shape. The Notes value isn't
 *  represented on PurchaseItem - it gets folded into the form-level Notes
 *  field by the caller. */
export function collectrToPurchaseItem(row: CollectrRow): ImportedPurchaseItem {
  const graded = isGraded(row);
  return {
    Type: graded ? "Graded" : "Raw",
    Game: row.category || "Pokemon",
    Item: row.productName,
    Set:  row.set,
    Number: row.cardNumber,
    Condition: graded ? "Near Mint" : conditionFor(row),
    Grade: graded ? normalizeGrade(row.grade) : "PSA 10",
    ProductType: "",
    Qty: row.quantity || 1,
    UnitCost: row.avgCostPaid || 0,
  };
}

/** Map a parsed Collectr row to an Adjustment "in"-mode item. The Market
 *  Price column drives ContribFMV (the fair market value being contributed);
 *  Average Cost Paid drives UnitBasis. Graded cards still map but with
 *  Condition set to the normalized grade (e.g. "PSA 10") since the
 *  Adjustment form doesn't have a separate Grade field. */
export function collectrToAdjustmentItem(row: CollectrRow): ImportedAdjustmentItem {
  return {
    AsType: "Card",
    Game: row.category || "Pokemon",
    Item: row.productName,
    Set:  row.set,
    Number: row.cardNumber,
    Condition: conditionFor(row),
    ProductType: "",
    Qty: row.quantity || 1,
    UnitBasis: row.avgCostPaid || 0,
    ContribFMV: row.marketPrice || 0,
  };
}

/** Map a parsed Collectr row to a Trade Incoming item. */
export function collectrToTradeIncoming(row: CollectrRow): ImportedTradeIncomingItem {
  const graded = isGraded(row);
  return {
    AsType: "Card",
    Game: row.category || "Pokemon",
    Item: row.productName,
    Set:  row.set,
    Number: row.cardNumber,
    Condition: conditionFor(row),
    ProductType: "",
    Image: "",
    Qty: row.quantity || 1,
    FMV: row.marketPrice || 0,
    CardKind: graded ? "Graded" : "Raw",
  };
}

/* ============================================================
   File-picker UI + dispatcher into the active form's state.
   Called from the Import buttons added to Purchase and Trade.
   ============================================================ */

/** Open the OS file picker, read the chosen file, parse, and append the
 *  resulting items to whichever form is currently in view. The form is
 *  detected by looking at the shared window state objects. */
export function triggerCollectrImport(target: "purchase" | "trade-incoming" | "adjustment-in"): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".csv,text/csv";
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", async () => {
    try {
      const file = input.files?.[0];
      if (!file) return;
      const text = await file.text();
      let rows: CollectrRow[];
      try {
        rows = parseCollectrCsv(text);
      } catch (e: any) {
        toast(`Couldn't read CSV: ${e?.message || e}`, "err");
        return;
      }
      if (!rows.length) {
        toast("CSV had no data rows to import.", "err");
        return;
      }
      if (target === "purchase") {
        appendToPurchase(rows);
      } else if (target === "trade-incoming") {
        appendToTradeIncoming(rows);
      } else {
        appendToAdjustmentItems(rows);
      }
    } finally {
      input.remove();
    }
  });
  input.click();
}

/** Append parsed rows to the Purchase form state. Skips silently if the
 *  Purchase form isn't currently open (state object missing). */
function appendToPurchase(rows: CollectrRow[]): void {
  const state = (window as any)._purchaseState;
  if (!state || !Array.isArray(state.Items)) {
    toast("Open a purchase before importing.", "err");
    return;
  }
  let appended = 0;
  let withNotes = 0;
  const extraNotes: string[] = [];
  for (const row of rows) {
    const item = collectrToPurchaseItem(row);
    state.Items.push(item);
    appended++;
    const n = notesFor(row);
    if (n) {
      extraNotes.push(`${row.productName}${row.cardNumber ? ` #${row.cardNumber}` : ""}: ${n}`);
      withNotes++;
    }
  }
  // Stamp Date Added (oldest in the import) onto the form's Date if it hasn't
  // been changed yet. This is a best-effort: skips if the user already typed
  // a date manually since the form opened.
  const oldest = rows
    .map(r => r.dateAdded)
    .filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d))
    .sort()[0];
  if (oldest) {
    const dateInput = document.querySelector<HTMLInputElement>('#purchaseForm [name="Date"]');
    if (dateInput && (dateInput.value === todayISO() || !dateInput.value)) {
      dateInput.value = oldest;
      state.Date = oldest;
    }
  }
  // Fold per-row variance/rarity notes into the form Notes textarea.
  if (extraNotes.length) {
    const notesEl = document.querySelector<HTMLTextAreaElement>('#purchaseForm [name="Notes"]');
    if (notesEl) {
      const sep = notesEl.value.trim() ? "\n\n" : "";
      notesEl.value = `${notesEl.value}${sep}Imported from Collectr (${appended} item${appended === 1 ? "" : "s"}):\n` + extraNotes.join("\n");
      state.Notes = notesEl.value;
    }
  }
  (window as any).renderPurchaseItemsList?.();
  (window as any).markModalDirty?.();
  toast(`Imported ${appended} item${appended === 1 ? "" : "s"} from Collectr${withNotes ? ` (${withNotes} with notes added)` : ""}.`);
}

/** Append parsed rows to the Trade form Incoming list. */
function appendToTradeIncoming(rows: CollectrRow[]): void {
  const state = (window as any)._tradeState;
  if (!state || !Array.isArray(state.Incoming)) {
    toast("Open a trade before importing.", "err");
    return;
  }
  let appended = 0;
  const extraNotes: string[] = [];
  for (const row of rows) {
    const item = collectrToTradeIncoming(row);
    state.Incoming.push(item);
    appended++;
    const n = notesFor(row);
    if (n) extraNotes.push(`${row.productName}${row.cardNumber ? ` #${row.cardNumber}` : ""}: ${n}`);
  }
  if (extraNotes.length) {
    const notesEl = document.querySelector<HTMLTextAreaElement>('#tradeForm [name="Notes"]');
    if (notesEl) {
      const sep = notesEl.value.trim() ? "\n\n" : "";
      notesEl.value = `${notesEl.value}${sep}Imported from Collectr (${appended} incoming item${appended === 1 ? "" : "s"}):\n` + extraNotes.join("\n");
      state.Notes = notesEl.value;
    }
  }
  (window as any).renderTradeIncomingList?.();
  (window as any).markModalDirty?.();
  toast(`Imported ${appended} incoming item${appended === 1 ? "" : "s"} from Collectr.`);
}

/** Append parsed rows to the Adjustment form Items list. Only valid in "in"
 *  mode (Owner Contribution / Owner Cash Contribution); the function still
 *  appends in other modes but the form may not render them correctly - the
 *  Import button is only shown in "in" mode to prevent that. */
function appendToAdjustmentItems(rows: CollectrRow[]): void {
  const state = (window as any)._txnState;
  if (!state || !Array.isArray(state.Items)) {
    toast("Open an adjustment before importing.", "err");
    return;
  }
  // Sanity guard: if the user somehow triggered the import while the form
  // isn't in "in" mode (Owner Contribution), refuse to corrupt the items list.
  if (state.mode && state.mode !== "in") {
    toast("Import only works for Owner Contribution adjustments.", "err");
    return;
  }
  let appended = 0;
  const extraNotes: string[] = [];
  for (const row of rows) {
    const item = collectrToAdjustmentItem(row);
    state.Items.push(item);
    appended++;
    const n = notesFor(row);
    if (n) extraNotes.push(`${row.productName}${row.cardNumber ? ` #${row.cardNumber}` : ""}: ${n}`);
  }
  if (extraNotes.length) {
    const notesEl = document.querySelector<HTMLTextAreaElement>('#txnForm [name="Notes"]');
    if (notesEl) {
      const sep = notesEl.value.trim() ? "\n\n" : "";
      notesEl.value = `${notesEl.value}${sep}Imported from Collectr (${appended} item${appended === 1 ? "" : "s"}):\n` + extraNotes.join("\n");
      state["Notes"] = notesEl.value;
    }
  }
  (window as any).renderTxnItemsList?.();
  (window as any).markModalDirty?.();
  toast(`Imported ${appended} contribution item${appended === 1 ? "" : "s"} at market value.`);
}

// Window bridges so inline onclick handlers in form HTML can call us.
(window as any).triggerCollectrImport = triggerCollectrImport;
